import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import {
  addOneToOneConversationComment,
  completeOneToOneConversation,
  getMyOneToOneConversations,
  getOneToOneConversation,
  getTeamOneToOneConversations,
  saveOneToOneConversation,
} from "./appraisalApi";
import {
  AppraisalGuideButton,
  AppraisalHelpModal,
  AppraisalSectionHeader,
} from "./AppraisalHelp";
import { AppraisalEmployeeCell, pickEmployeeFields } from "./AppraisalEmployeeCell";
import {
  APPRAISAL_LIMITS,
  clampLen,
  hasAnyTrimmed,
  validateFields,
} from "./appraisalValidation";
import { AppraisalCharCount } from "./AppraisalFormFields";
import "./Appraisal.css";

const emptyForm = () => ({
  id: "",
  wellbeing: "",
  currentPerformance: "",
  motivatesOrDemotivates: "",
  recentlyDone: "",
  housekeeping: "",
  isCompleted: false,
});

const TOPIC_FIELDS = [
  {
    key: "wellbeing",
    icon: "bi-heart-pulse",
    labelDefault: "Wellbeing",
    hintDefault: "How are you feeling at work? Energy, stress, support?",
    placeholderDefault: "Share how things feel right now…",
  },
  {
    key: "currentPerformance",
    icon: "bi-graph-up-arrow",
    labelDefault: "Current performance",
    hintDefault: "What is going well, and where do you need support?",
    placeholderDefault: "Progress, blockers, and priorities…",
  },
  {
    key: "motivatesOrDemotivates",
    icon: "bi-lightning-charge",
    labelDefault: "What motivates / demotivates",
    hintDefault: "What increases or reduces your motivation lately?",
    placeholderDefault: "Drivers, frustrations, or ideas to improve…",
  },
  {
    key: "recentlyDone",
    icon: "bi-check2-square",
    labelDefault: "Recently done",
    hintDefault: "Wins, deliveries, or learning from the last period.",
    placeholderDefault: "Key outcomes or work completed…",
  },
  {
    key: "housekeeping",
    icon: "bi-clipboard-check",
    labelDefault: "Housekeeping",
    hintDefault: "Admin, leave, tools, process, or anything else to clear.",
    placeholderDefault: "Practical items to follow up…",
  },
];

const normalizeConversation = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const employee = pickEmployeeFields(raw);
  return {
    ...raw,
    id: raw.id ?? raw.Id,
    userId: raw.userId ?? raw.UserId ?? "",
    ...employee,
    managerId: raw.managerId ?? raw.ManagerId ?? "",
    wellbeing: raw.wellbeing ?? raw.Wellbeing ?? "",
    currentPerformance: raw.currentPerformance ?? raw.CurrentPerformance ?? "",
    motivatesOrDemotivates: raw.motivatesOrDemotivates ?? raw.MotivatesOrDemotivates ?? "",
    recentlyDone: raw.recentlyDone ?? raw.RecentlyDone ?? "",
    housekeeping: raw.housekeeping ?? raw.Housekeeping ?? "",
    isCompleted: Boolean(raw.isCompleted ?? raw.IsCompleted),
    createdDate: raw.createdDate ?? raw.CreatedDate,
    comments: raw.comments ?? raw.Comments ?? [],
  };
};

const normalizeComment = (c) => ({
  id: c.id ?? c.Id,
  userId: c.userId ?? c.UserId ?? "",
  userName: c.userName ?? c.UserName ?? "",
  commentText: c.commentText ?? c.CommentText ?? "",
  dateAdded: c.dateAdded ?? c.DateAdded,
});

const conversationSummary = (r, t) =>
  r.wellbeing ||
  r.currentPerformance ||
  r.recentlyDone ||
  r.motivatesOrDemotivates ||
  r.housekeeping ||
  t("app.appraisal.conversationUntitled", {
    defaultValue: "Conversation check-in",
  });

/** mode: "mine" | "team" | "detail" */
const AppraisalConversationPage = ({ mode = "mine" }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [rows, setRows] = useState([]);
  const [item, setItem] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [showForm, setShowForm] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [helpKey, setHelpKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [postingComment, setPostingComment] = useState(false);
  const [teamDetailId, setTeamDetailId] = useState(null);

  const isTeam = mode === "team";
  const detailId = isTeam ? teamDetailId : id || null;
  const showingDetail = Boolean(detailId) || mode === "detail";
  const fromTeam =
    isTeam ||
    location.state?.from === "team" ||
    new URLSearchParams(location.search || "").get("from") === "team";

  const title = useMemo(() => {
    if (isTeam)
      return t("app.appraisal.teamConversationTitle", {
        defaultValue: "Team Conversations",
      });
    if (showingDetail)
      return t("app.appraisal.conversationDetailTitle", {
        defaultValue: "Conversation detail",
      });
    return t("app.appraisal.myConversationTitle", {
      defaultValue: "My Conversations",
    });
  }, [showingDetail, isTeam, t]);

  const openCount = useMemo(
    () => rows.filter((r) => !r.isCompleted).length,
    [rows]
  );

  const load = useCallback(
    async ({ silent } = {}) => {
      if (!silent) setLoading(true);
      setError("");
      try {
        if (detailId) {
          const detail = normalizeConversation(await getOneToOneConversation(detailId));
          setItem(detail);
          setForm({
            id: detail.id,
            wellbeing: detail.wellbeing || "",
            currentPerformance: detail.currentPerformance || "",
            motivatesOrDemotivates: detail.motivatesOrDemotivates || "",
            recentlyDone: detail.recentlyDone || "",
            housekeeping: detail.housekeeping || "",
            isCompleted: !!detail.isCompleted,
          });
        } else {
          const list = isTeam
            ? await getTeamOneToOneConversations()
            : await getMyOneToOneConversations();
          setRows(
            (Array.isArray(list) ? list : []).map(normalizeConversation).filter(Boolean)
          );
        }
      } catch (e) {
        setError(e?.message || "Failed to load conversations");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [detailId, isTeam]
  );

  useEffect(() => {
    load();
  }, [load]);

  const openNewForm = () => {
    setForm(emptyForm());
    setShowForm(true);
    setError("");
    setMessage("");
  };

  const openItem = (itemId) => {
    if (isTeam) {
      setTeamDetailId(itemId);
      setError("");
      setMessage("");
      return;
    }
    history.push(`/appraisal/conversations/${itemId}`);
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (saving) return;
    setMessage("");
    setError("");
    const maxLenError = validateFields([
      { value: form.wellbeing, max: APPRAISAL_LIMITS.longText, label: "Wellbeing" },
      {
        value: form.currentPerformance,
        max: APPRAISAL_LIMITS.longText,
        label: "Current performance",
      },
      {
        value: form.motivatesOrDemotivates,
        max: APPRAISAL_LIMITS.longText,
        label: "What motivates / demotivates",
      },
      { value: form.recentlyDone, max: APPRAISAL_LIMITS.longText, label: "Recently done" },
      { value: form.housekeeping, max: APPRAISAL_LIMITS.longText, label: "Housekeeping" },
    ]);
    if (maxLenError) {
      setError(maxLenError);
      return;
    }
    if (
      !showingDetail &&
      !hasAnyTrimmed(
        form.wellbeing,
        form.currentPerformance,
        form.motivatesOrDemotivates,
        form.recentlyDone,
        form.housekeeping
      )
    ) {
      setError(
        t("app.appraisal.conversations.emptyError", {
          defaultValue: "Add notes to at least one conversation topic.",
        })
      );
      return;
    }
    setSaving(true);
    try {
      const saved = await saveOneToOneConversation(form);
      setMessage(
        t("app.appraisal.conversations.saved", {
          defaultValue: "Conversation saved.",
        })
      );
      setShowForm(false);
      if (saved?.id && !showingDetail) {
        history.push(`/appraisal/conversations/${saved.id}`);
      } else {
        await load({ silent: true });
      }
    } catch (err) {
      setError(err?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const onComplete = async () => {
    if (completing || !item?.id) return;
    setCompleting(true);
    setError("");
    try {
      await completeOneToOneConversation({ conversationId: item.id });
      setMessage(
        t("app.appraisal.conversations.completed", {
          defaultValue: "Marked complete.",
        })
      );
      await load({ silent: true });
    } catch (err) {
      setError(err?.message || "Failed to complete");
    } finally {
      setCompleting(false);
    }
  };

  const onComment = async () => {
    if (!commentText.trim() || postingComment || !item?.id) return;
    setPostingComment(true);
    setError("");
    try {
      await addOneToOneConversationComment({
        conversationId: item.id,
        commentText,
      });
      setCommentText("");
      setMessage(
        t("app.appraisal.conversations.commentAdded", {
          defaultValue: "Comment added.",
        })
      );
      await load({ silent: true });
    } catch (err) {
      setError(err?.message || "Failed to add comment");
    } finally {
      setPostingComment(false);
    }
  };

  const comments = (item?.comments || []).map(normalizeComment);

  const renderTopicFields = ({ disabled = false } = {}) =>
    TOPIC_FIELDS.map((field) => (
      <label key={field.key} className="appraisal-field appraisal-conversation-topic">
        <span className="appraisal-conversation-topic__label">
          <i className={`bi ${field.icon}`} aria-hidden />
          {t(`app.appraisal.conversations.${field.key}`, {
            defaultValue: field.labelDefault,
          })}
        </span>
        <small className="appraisal-field-hint">
          {t(`app.appraisal.conversations.${field.key}Hint`, {
            defaultValue: field.hintDefault,
          })}
        </small>
        <textarea
          rows={3}
          disabled={disabled || saving}
          maxLength={APPRAISAL_LIMITS.longText}
          placeholder={t(`app.appraisal.conversations.${field.key}Placeholder`, {
            defaultValue: field.placeholderDefault,
          })}
          value={form[field.key]}
          onChange={(e) =>
            setForm((p) => ({
              ...p,
              [field.key]: clampLen(e.target.value, APPRAISAL_LIMITS.longText),
            }))
          }
        />
        <AppraisalCharCount value={form[field.key]} max={APPRAISAL_LIMITS.longText} />
      </label>
    ));

  return (
    <div
      className={`appraisal-page${!isTeam && !showingDetail ? " appraisal-my-conversation-page" : ""}`}
    >
      <CommonPageHeader
        icon={<i className="bi bi-chat-square-heart" />}
        title={title}
        description={
          showingDetail
            ? item?.userName || ""
            : isTeam
              ? t("app.appraisal.teamConversationSubtitle", {
                  defaultValue:
                    "Review structured check-ins for your team — wellbeing, performance, and housekeeping.",
                })
              : t("app.appraisal.conversationSubtitle", {
                  defaultValue:
                    "Structured check-ins covering wellbeing, performance, motivation, and housekeeping.",
                })
        }
        rightContent={
          !showingDetail ? (
            <div className="appraisal-page-header-actions">
              <AppraisalGuideButton onClick={() => setHelpKey("conversations")} />
              {!isTeam ? (
                <button
                  type="button"
                  className="appraisal-btn primary appraisal-header-cta"
                  onClick={openNewForm}
                >
                  <i className="bi bi-plus-lg" aria-hidden />
                  {t("app.appraisal.conversations.new", {
                    defaultValue: "New conversation",
                  })}
                </button>
              ) : null}
            </div>
          ) : null
        }
      />
      {loading ? <div className="appraisal-muted">Loading…</div> : null}
      {error ? <div className="appraisal-error">{error}</div> : null}
      {message ? <div className="appraisal-success">{message}</div> : null}

      {!showingDetail && !isTeam ? (
        <section className="appraisal-settings-panel appraisal-conversation-panel">
          <div className="appraisal-settings-panel__head appraisal-goals-panel__head">
            <div>
              <AppraisalSectionHeader
                icon="bi-chat-square-heart"
                title={t("app.appraisal.conversations.helpCardTitle", {
                  defaultValue: "My conversations",
                })}
                helpKey="conversations"
                onOpenHelp={setHelpKey}
                tone="conversations"
              />
              <p className="appraisal-framework-hint">
                {t("app.appraisal.conversations.hint", {
                  defaultValue:
                    "Guided 1:1 topics — wellbeing, performance, motivation, recent work, and housekeeping.",
                })}
              </p>
            </div>
            <div className="appraisal-goals-panel__stats">
              <div className="appraisal-goals-stat">
                <strong>{rows.length}</strong>
                <span>
                  {t("app.appraisal.conversations.totalLabel", {
                    defaultValue: "Total",
                  })}
                </span>
              </div>
              <div className="appraisal-goals-stat">
                <strong>{openCount}</strong>
                <span>
                  {t("app.appraisal.conversations.openLabel", {
                    defaultValue: "Open",
                  })}
                </span>
              </div>
            </div>
          </div>

          {showForm ? (
            <form
              onSubmit={onSave}
              className="appraisal-framework-form appraisal-conversation-create"
            >
              <div className="appraisal-pdp-create__intro">
                <div className="appraisal-pdp-create__icon" aria-hidden>
                  <i className="bi bi-chat-dots" />
                </div>
                <div>
                  <strong>
                    {t("app.appraisal.conversations.createTitle", {
                      defaultValue: "Start a structured conversation",
                    })}
                  </strong>
                  <p>
                    {t("app.appraisal.conversations.createHint", {
                      defaultValue:
                        "Fill in the topics that matter for this check-in. You can leave some blank and complete the rest later.",
                    })}
                  </p>
                </div>
              </div>

              <div className="appraisal-pdp-steps" aria-hidden>
                <span className="appraisal-pdp-steps__item is-current">
                  <em>1</em>
                  {t("app.appraisal.conversations.stepPrepare", {
                    defaultValue: "Prepare",
                  })}
                </span>
                <i className="bi bi-chevron-right" />
                <span className="appraisal-pdp-steps__item">
                  <em>2</em>
                  {t("app.appraisal.conversations.stepDiscuss", {
                    defaultValue: "Discuss",
                  })}
                </span>
                <i className="bi bi-chevron-right" />
                <span className="appraisal-pdp-steps__item">
                  <em>3</em>
                  {t("app.appraisal.conversations.stepComplete", {
                    defaultValue: "Complete",
                  })}
                </span>
              </div>

              <div className="appraisal-conversation-topics">{renderTopicFields()}</div>

              <div className="appraisal-framework-form__actions">
                <button
                  type="button"
                  className="appraisal-btn"
                  disabled={saving}
                  onClick={() => {
                    setShowForm(false);
                    setForm(emptyForm());
                  }}
                >
                  {t("app.common.cancel", { defaultValue: "Cancel" })}
                </button>
                <button type="submit" className="appraisal-btn primary" disabled={saving}>
                  {saving
                    ? t("app.common.saving", { defaultValue: "Saving…" })
                    : t("app.appraisal.conversations.save", {
                        defaultValue: "Save conversation",
                      })}
                </button>
              </div>
            </form>
          ) : null}

          <div className="appraisal-framework-list-block">
            <div className="appraisal-framework-list-block__head">
              <span>
                {t("app.appraisal.conversations.listTitle", {
                  defaultValue: "Your conversations",
                })}
              </span>
              <span className="appraisal-framework-count">{rows.length}</span>
            </div>

            {!loading && rows.length === 0 ? (
              <div className="appraisal-framework-empty">
                <i className="bi bi-chat-square-heart" aria-hidden />
                <p>
                  {t("app.appraisal.noConversations", {
                    defaultValue:
                      "No conversations yet. Start a structured check-in with your manager.",
                  })}
                </p>
                {!showForm ? (
                  <button
                    type="button"
                    className="appraisal-btn primary"
                    onClick={openNewForm}
                  >
                    <i className="bi bi-plus-lg" aria-hidden />
                    {t("app.appraisal.conversations.new", {
                      defaultValue: "New conversation",
                    })}
                  </button>
                ) : null}
              </div>
            ) : null}

            {!loading && rows.length > 0 ? (
              <div className="appraisal-table-wrap appraisal-team-table-wrap appraisal-goals-table-wrap">
                <table className="appraisal-table appraisal-team-table">
                  <thead>
                    <tr>
                      <th>
                        {t("app.appraisal.colSummary", { defaultValue: "Summary" })}
                      </th>
                      <th>{t("app.appraisal.colDate", { defaultValue: "Date" })}</th>
                      <th>{t("app.appraisal.colStatus", { defaultValue: "Status" })}</th>
                      <th className="appraisal-team-col-actions">
                        {t("app.appraisal.colActions", { defaultValue: "Actions" })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td>
                          <div className="appraisal-goal-headline appraisal-conversation-summary">
                            {conversationSummary(r, t)}
                          </div>
                        </td>
                        <td className="appraisal-team-col-date">
                          {r.createdDate
                            ? new Date(r.createdDate).toLocaleDateString()
                            : "—"}
                        </td>
                        <td>
                          <span
                            className={`appraisal-status-pill ${
                              r.isCompleted
                                ? "appraisal-status-pill--oto-done"
                                : "appraisal-status-pill--oto-open"
                            }`}
                          >
                            {r.isCompleted
                              ? t("app.appraisal.statusCompleted", {
                                  defaultValue: "Completed",
                                })
                              : t("app.appraisal.statusOpen", {
                                  defaultValue: "Open",
                                })}
                          </span>
                        </td>
                        <td className="appraisal-team-col-actions">
                          <button
                            type="button"
                            className="appraisal-btn primary appraisal-team-action"
                            onClick={() => openItem(r.id)}
                          >
                            <i className="bi bi-box-arrow-up-right" aria-hidden />
                            {t("app.appraisal.open", { defaultValue: "Open" })}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {!showingDetail && isTeam ? (
        <>
          <section className="appraisal-card">
            <AppraisalSectionHeader
              icon="bi-chat-square-heart"
              title={t("app.appraisal.conversations.helpCardTitle", {
                defaultValue: "Team conversations",
              })}
              helpKey="conversations"
              onOpenHelp={setHelpKey}
              tone="conversations"
            />
            <p className="appraisal-framework-hint" style={{ marginBottom: 0 }}>
              {t("app.appraisal.conversations.teamHint", {
                defaultValue:
                  "Review structured check-ins for your reports — wellbeing, performance, and housekeeping.",
              })}
            </p>
          </section>

          {!loading && rows.length === 0 ? (
            <div className="appraisal-team-empty">
              <i className="bi bi-chat-square-heart" aria-hidden />
              <p>
                {t("app.appraisal.noTeamConversations", {
                  defaultValue: "No team conversations yet.",
                })}
              </p>
            </div>
          ) : null}

          {!loading && rows.length > 0 ? (
            <div className="appraisal-table-wrap appraisal-team-table-wrap">
              <table className="appraisal-table appraisal-team-table">
                <thead>
                  <tr>
                    <th className="appraisal-team-col-employee">
                      {t("app.appraisal.colEmployee", { defaultValue: "Employee" })}
                    </th>
                    <th>
                      {t("app.appraisal.colSummary", { defaultValue: "Summary" })}
                    </th>
                    <th>{t("app.appraisal.colDate", { defaultValue: "Date" })}</th>
                    <th>{t("app.appraisal.colStatus", { defaultValue: "Status" })}</th>
                    <th className="appraisal-team-col-actions">
                      {t("app.appraisal.colActions", { defaultValue: "Actions" })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className="appraisal-team-col-employee">
                        <AppraisalEmployeeCell
                          name={r.userName}
                          designation={r.designation}
                          department={r.department}
                          officeName={r.officeName}
                          profileImageUrl={r.profileImageUrl}
                        />
                      </td>
                      <td>
                        <div className="appraisal-goal-headline appraisal-conversation-summary">
                          {conversationSummary(r, t)}
                        </div>
                      </td>
                      <td className="appraisal-team-col-date">
                        {r.createdDate
                          ? new Date(r.createdDate).toLocaleDateString()
                          : "—"}
                      </td>
                      <td>
                        <span
                          className={`appraisal-status-pill ${
                            r.isCompleted
                              ? "appraisal-status-pill--oto-done"
                              : "appraisal-status-pill--oto-open"
                          }`}
                        >
                          {r.isCompleted
                            ? t("app.appraisal.statusCompleted", {
                                defaultValue: "Completed",
                              })
                            : t("app.appraisal.statusOpen", {
                                defaultValue: "Open",
                              })}
                        </span>
                      </td>
                      <td className="appraisal-team-col-actions">
                        <button
                          type="button"
                          className="appraisal-btn primary appraisal-team-action"
                          onClick={() => openItem(r.id)}
                        >
                          <i className="bi bi-box-arrow-up-right" aria-hidden />
                          {t("app.appraisal.open", { defaultValue: "Open" })}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : null}

      {showingDetail && item ? (
        <>
          <div className="appraisal-card appraisal-checkin-detail">
            {isTeam ? (
              <button
                type="button"
                className="appraisal-checkin-back"
                onClick={() => {
                  setTeamDetailId(null);
                  setItem(null);
                  setCommentText("");
                }}
              >
                <i className="bi bi-arrow-left" aria-hidden />
                {t("app.appraisal.back", { defaultValue: "Back" })}
              </button>
            ) : (
              <Link
                className="appraisal-checkin-back"
                to={fromTeam ? "/appraisal/team-conversations" : "/appraisal/conversations"}
              >
                <i className="bi bi-arrow-left" aria-hidden />
                {t("app.appraisal.back", { defaultValue: "Back" })}
              </Link>
            )}
            <div className="appraisal-checkin-detail__head">
              <div>
                <h3>
                  {item.userName ||
                    t("app.appraisal.conversations.untitled", {
                      defaultValue: "Conversation check-in",
                    })}
                </h3>
                <p className="appraisal-checkin-detail__meta">
                  {item.designation ? <span>{item.designation}</span> : null}
                  {item.createdDate ? (
                    <span>{new Date(item.createdDate).toLocaleDateString()}</span>
                  ) : null}
                </p>
              </div>
              <span
                className={`appraisal-status-pill ${
                  item.isCompleted
                    ? "appraisal-status-pill--oto-done"
                    : "appraisal-status-pill--oto-open"
                }`}
              >
                {item.isCompleted
                  ? t("app.appraisal.statusCompleted", { defaultValue: "Completed" })
                  : t("app.appraisal.statusOpen", { defaultValue: "Open" })}
              </span>
            </div>
          </div>

          <form className="appraisal-card" onSubmit={onSave}>
            <h4>
              {item.isCompleted
                ? t("app.appraisal.conversations.topicsReadOnly", {
                    defaultValue: "Conversation topics",
                  })
                : t("app.appraisal.conversations.editTitle", {
                    defaultValue: "Update conversation topics",
                  })}
            </h4>
            {!item.isCompleted ? (
              <p className="appraisal-framework-hint">
                {t("app.appraisal.conversations.editHint", {
                  defaultValue:
                    "Capture notes under each prompt during or after the discussion, then mark complete.",
                })}
              </p>
            ) : null}
            <div className="appraisal-conversation-topics">
              {renderTopicFields({ disabled: item.isCompleted })}
            </div>
            {!item.isCompleted ? (
              <div className="appraisal-framework-form__actions">
                <button
                  type="button"
                  className="appraisal-btn"
                  disabled={completing || saving}
                  onClick={onComplete}
                >
                  {completing
                    ? t("app.common.saving", { defaultValue: "Saving…" })
                    : t("app.appraisal.markComplete", {
                        defaultValue: "Mark complete",
                      })}
                </button>
                <button type="submit" className="appraisal-btn primary" disabled={saving}>
                  {saving
                    ? t("app.common.saving", { defaultValue: "Saving…" })
                    : t("app.appraisal.saveChanges", {
                        defaultValue: "Save changes",
                      })}
                </button>
              </div>
            ) : null}
          </form>

          <section className="appraisal-card appraisal-comments-panel">
            <div className="appraisal-comments-panel__head">
              <span className="appraisal-comments-panel__icon" aria-hidden>
                <i className="bi bi-chat-left-text" />
              </span>
              <div>
                <h4>{t("app.appraisal.comments", { defaultValue: "Comments" })}</h4>
                <p>
                  {t("app.appraisal.conversations.commentsHint", {
                    defaultValue: "Discuss progress, blockers, or next steps.",
                  })}
                </p>
              </div>
            </div>
            <div className="appraisal-comments-composer">
              <label className="appraisal-field">
                {t("app.appraisal.addComment", { defaultValue: "Add comment" })}
                <textarea
                  rows={2}
                  disabled={postingComment}
                  maxLength={APPRAISAL_LIMITS.comment}
                  value={commentText}
                  onChange={(e) =>
                    setCommentText(clampLen(e.target.value, APPRAISAL_LIMITS.comment))
                  }
                  placeholder={t("app.appraisal.conversations.commentPlaceholder", {
                    defaultValue: "Add a follow-up note or clarification…",
                  })}
                />
                <AppraisalCharCount value={commentText} max={APPRAISAL_LIMITS.comment} />
              </label>
              <button
                type="button"
                className="appraisal-btn"
                disabled={!commentText.trim() || postingComment}
                onClick={onComment}
              >
                {postingComment
                  ? t("app.common.saving", { defaultValue: "Saving…" })
                  : t("app.appraisal.postComment", { defaultValue: "Post comment" })}
              </button>
            </div>
            {comments.length > 0 ? (
              <ul className="appraisal-checkin-comments">
                {comments.map((c) => (
                  <li key={c.id}>
                    <div className="appraisal-checkin-comments__head">
                      <strong>{c.userName || c.userId || "—"}</strong>
                      <span>
                        {c.dateAdded ? new Date(c.dateAdded).toLocaleString() : ""}
                      </span>
                    </div>
                    <p>{c.commentText}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="appraisal-muted" style={{ marginTop: "1rem", marginBottom: 0 }}>
                {t("app.appraisal.noComments", { defaultValue: "No comments yet." })}
              </p>
            )}
          </section>
        </>
      ) : null}

      <AppraisalHelpModal helpKey={helpKey} onClose={() => setHelpKey(null)} />
    </div>
  );
};

export default AppraisalConversationPage;
